import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CircleAlert } from 'lucide-react';
import ProductCard, { ProductCardSkeleton } from '../../../components/productCard';
import { productsAPI } from '../../../api/apiService';
import { getErrorMessage } from '../../../utils/apiError';

const FETCH_COUNT = 8;
const SHOW_COUNT = 4;

/** "You may also like": other projects in the same category. Hidden when there are none. */
const RelatedProjects = ({ productId, category }) => {
  const [state, setState] = useState({ status: 'loading', items: [], error: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!category) {
      setState({ status: 'ready', items: [], error: null });
      return undefined;
    }
    let active = true;
    setState({ status: 'loading', items: [], error: null });
    productsAPI
      .getAll({ category, itemsPerPage: FETCH_COUNT })
      .then(response => {
        if (!active) return;
        const items = (response.data?.data?.products || [])
          .filter(p => p?.id && p.id !== productId)
          .slice(0, SHOW_COUNT);
        setState({ status: 'ready', items, error: null });
      })
      .catch(error => {
        if (!active) return;
        setState({
          status: 'error',
          items: [],
          error: getErrorMessage(error, "We couldn't load similar projects."),
        });
      });
    return () => {
      active = false;
    };
  }, [productId, category, reloadKey]);

  if (state.status === 'ready' && state.items.length === 0) return null;

  return (
    <section className="pd-related" aria-labelledby="pd-related-title">
      <div className="pd-related__head">
        <h2 id="pd-related-title" className="pk-title-2">
          You may also like
        </h2>
        <Link
          className="pd-related__more"
          to={`/products?category=${encodeURIComponent(category)}`}
        >
          More in {category}
          <ArrowRight aria-hidden="true" />
        </Link>
      </div>

      {state.status === 'error' ? (
        <div className="pk-alert" data-tone="danger" role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{state.error}</span>
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--xs"
            onClick={() => setReloadKey(k => k + 1)}
          >
            Retry
          </button>
        </div>
      ) : (
        <ul className="pd-related__grid" aria-busy={state.status === 'loading' || undefined}>
          {state.status === 'loading'
            ? Array.from({ length: SHOW_COUNT }, (_, i) => (
                <li key={i}>
                  <ProductCardSkeleton />
                </li>
              ))
            : state.items.map(product => (
                <li key={product.id}>
                  <ProductCard product={product} />
                </li>
              ))}
        </ul>
      )}
    </section>
  );
};

export default RelatedProjects;
